// Shopify Forms app-embed styling, shared by every section that hosts the
// Home Club form (sections/form-media.liquid, sections/cart.liquid).
//
// The Shopify Forms app renders inside an open shadow root with its own
// styles, exposing no ::part hooks - so theme CSS can't reach the fields.
// We append an override stylesheet into that shadow root to match the
// design. CSS custom properties cross the shadow boundary, so design tokens
// resolve from :root and sections can retheme the form by setting the
// --esr-forms-* properties on any ancestor of the embed:
//
//   --esr-forms-btn-bg / --esr-forms-btn-color   submit button colours
//   --esr-forms-input-color                       input text colour
//   --esr-forms-underline                         input underline colour
//   --esr-forms-phone-display / --esr-forms-phone-cols
//     set to `none` / `1fr` to hide the phone country (flag) picker
//
// Hooks read from the light DOM:
//   [data-phone-country-lock="AU|NZ"]  nearest ancestor of the embed - locks
//     the (hidden) phone country picker to that country.
//   [data-esr-forms-reveal]  optional wrapper that starts at opacity:0 (the
//     hosting section's CSS) and gets .is-ready once the form is styled, so
//     the raw app styles never flash.
//   [data-esr-forms-account-email]  nearest ancestor of the embed - the
//     signed-in account's email address, used to decide whether a ?email=
//     query string is the account's own address or a different one.
//
// Query-string prefill: the footer "Join Home Club" signup and the newsletter
// signup section are GET forms pointing at the page that hosts this form, so
// arriving here carries ?email=<what they typed>. That value is written into the
// matching field so the visitor doesn't type it twice.
//
// Signed in, the app fills the whole form from the account, which used to win
// over the address the visitor had just typed. Now the two are compared: the
// same address means the account's details are the ones wanted, so the form is
// left alone; a different address means the one they typed wins, and the
// account's other details are cleared rather than carried across.
//
// Selectors target the app's CSS-module name prefixes (stable across builds;
// the hash suffix is ignored) - re-check on app upgrades.
(function () {
  if (window.__esrFormsStyling) return;
  window.__esrFormsStyling = true;

  var FORM_CSS = `
    /* Field wrapper -> theme .field (label above, stacked) */
    [class*="_formFieldContainer_"] {
      display: flex !important;
      flex-direction: column !important;
      gap: var(--spacing-4) !important;
      width: 100% !important;
    }
    /* Even vertical rhythm: a small inter-row gap plus a matching top margin
       on every field row after the first. Error outputs (also fieldset
       children) only pick up the gap, so they stay tight to their own field.
       The phone + labelled-select rows use their own wrapper classes, so they
       get the margin too (the phone flag's unlabelled select is excluded). */
    [class*="_formFieldset_"] {
      gap: var(--spacing-12) !important;
    }
    [class*="_formFieldContainer_"] ~ [class*="_formFieldContainer_"],
    [class*="_formFieldContainer_"] ~ [class*="_formPhoneInputContainer_"],
    [class*="_formFieldContainer_"] ~ [class*="_selectContainer_"]:has([class*="_label_"]) {
      margin-top: var(--spacing-12) !important;
    }

    /* Strip the app's container chrome: the form container's 2px margin, its
       own width cap (450px - the host section's wrapper is the width
       authority), its min-height (leaves a hole under the success banner)
       and the grid item's padding. */
    [class*="_formContainer_"] {
      margin: 0 !important;
      width: 100% !important;
      max-width: none !important;
      min-height: 0 !important;
    }
    [class*="_gridItemContent_"] {
      padding: 0 !important;
    }

    /* Label -> theme .field__label. The app floats the label (absolute,
       inline top); force it static above the control. */
    [class*="_formInputFieldLabel_"] {
      position: static !important;
      top: auto !important;
      left: auto !important;
      transform: none !important;
      order: -1 !important;
      padding: 0 !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-fog) !important;
      /* Float-label: hidden until the field is focused or has a value, then
         fades in (the placeholder is the empty-state cue). The Preferred Store
         select uses its own _label_ class, so it is unaffected. */
      opacity: 0 !important;
      transition: opacity 0.2s ease !important;
    }
    [class*="_formFieldContainer_"]:focus-within [class*="_formInputFieldLabel_"],
    [class*="_formInputFieldLabel_"][class*="_inputFilled_"],
    [class*="_formFieldContainer_"]:has([class*="_formInputField_"]:not(:placeholder-shown)) [class*="_formInputFieldLabel_"],
    [class*="_formFieldContainer_"]:has([class*="_formPhoneInputField_"]:not(:placeholder-shown)) [class*="_formInputFieldLabel_"] {
      opacity: 1 !important;
    }

    /* The select/dropdown (e.g. Preferred Store) uses its own _label_ class
       from the select module, not _formInputFieldLabel_; style it to match. */
    [class*="_selectContainer_"] [class*="_label_"] {
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-fog) !important;
    }

    /* Inputs / phone / select -> theme .field__control (30px underline). The
       app draws its box via box-shadow + a font shorthand; both are reset. */
    [class*="_formInputField_"],
    [class*="_formPhoneInputField_"],
    [class*="_formSelect"],
    select {
      width: 100% !important;
      height: 30px !important;
      box-sizing: border-box !important;
      padding: 0 0 var(--spacing-4) !important;
      border: 0 !important;
      border-bottom: 1px solid var(--esr-forms-underline, var(--color-mist)) !important;
      border-radius: var(--radius-none) !important;
      box-shadow: none !important;
      background: transparent !important;
      color: var(--esr-forms-input-color, var(--color-dark-oak)) !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-large) !important;
      line-height: var(--line-height-body) !important;
      -webkit-appearance: none !important;
      appearance: none !important;
    }
    @media (min-width: 1104px) {
      [class*="_formInputField_"],
      [class*="_formPhoneInputField_"],
      [class*="_formSelect"],
      select {
        font-size: var(--text-body-medium) !important;
      }
    }
    [class*="_formInputField_"]::placeholder,
    [class*="_formPhoneInputField_"]::placeholder {
      color: var(--color-warm-grey) !important;
      font-size: var(--text-body-medium) !important;
      /* The app hides the native placeholder with opacity:0 for its floating
         label; restore it so our placeholder text shows. */
      opacity: 1 !important;
    }
    [class*="_formInputField_"]:hover:not(:disabled),
    [class*="_formPhoneInputField_"]:hover:not(:disabled),
    [class*="_formSelect"]:hover:not(:disabled),
    select:hover:not(:disabled) {
      border-bottom-color: var(--color-warm-grey) !important;
    }
    [class*="_formInputField_"]:focus-visible,
    [class*="_formPhoneInputField_"]:focus-visible,
    [class*="_formSelect"]:focus-visible,
    select:focus-visible {
      outline: none !important;
      border-bottom-color: var(--color-dark-oak) !important;
      box-shadow: none !important;
    }

    /* Phone country (flag) selector -> match the underline inputs: drop the
       boxed white toggle, sit the flag + arrow on the same 30px underline as
       the "+61" field, and recolour the grey arrow to dark-oak. */
    [class*="_formPhoneInputContainer_"] {
      grid-template-columns: var(--esr-forms-phone-cols, auto 1fr) !important;
      align-items: end !important;
    }
    .phone-country-selector,
    [class*="_selectContainer_"] {
      height: auto !important;
    }
    /* When the section locks the phone country, hide the flag picker (display
       driven by --esr-forms-phone-display; the row also collapses to one
       column above). It stays in the DOM so the JS can set the locked country;
       display:none keeps it out of the layout and away from the user. */
    .phone-country-selector {
      display: var(--esr-forms-phone-display, block) !important;
    }
    [class*="_selectToggle_"] {
      height: 30px !important;
      box-sizing: border-box !important;
      gap: var(--spacing-12) !important;
      padding: 0 0 var(--spacing-4) !important;
      background: transparent !important;
      border: 0 !important;
      border-bottom: 1px solid var(--esr-forms-underline, var(--color-mist)) !important;
      border-radius: var(--radius-none) !important;
      box-shadow: none !important;
    }
    /* The chosen value (e.g. "NSW - Newcastle") is a plain span, not a form
       control, so match it to the input text: Manrope, body size, ink colour. */
    [class*="_selectToggleText_"] {
      width: auto !important;
      color: var(--esr-forms-input-color, var(--color-dark-oak)) !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-large) !important;
      line-height: var(--line-height-body) !important;
    }
    /* Own media query (after the base rule above) so the desktop step wins -
       media queries add no specificity, so it must follow in source order. */
    @media (min-width: 1104px) {
      [class*="_selectToggleText_"] {
        font-size: var(--text-body-medium) !important;
      }
    }
    [class*="_arrowDownIcon_"] {
      margin: 0 !important;
    }
    [class*="_arrowDownIcon_"] svg {
      width: 12px !important;
      height: 12px !important;
      display: block !important;
    }
    [class*="_arrowDownIcon_"] svg path {
      fill: none !important;
      stroke: var(--color-dark-oak) !important;
    }

    /* Custom select (e.g. Preferred Store) has its own <p> label class
       (not _formInputFieldLabel_), so map it to the theme .field__label and
       stack it above the toggle with the standard 4px gap. :has() limits the
       flex change to the labelled select - the phone-country selector reuses
       _selectContainer_ without a label. */
    [class*="_selectContainer_"]:has([class*="_label_"]) {
      display: flex !important;
      flex-direction: column !important;
      gap: var(--spacing-4) !important;
    }
    [class*="_selectContainer_"] [class*="_label_"] {
      margin: 0 !important;
      padding: 0 !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-fog) !important;
    }

    /* Checkbox -> brand 14px square, dark-oak */
    input[type="checkbox"] {
      accent-color: var(--color-dark-oak) !important;
      width: 14px !important;
      height: 14px !important;
      border-radius: var(--radius-none) !important;
    }

    /* Submit -> theme .button--solid.button--dark-oak.button--medium.button--full (40px) */
    [class*="_formSubmitButton_"] {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 40px !important;
      margin-top: var(--spacing-16) !important;
      box-sizing: border-box !important;
      padding: 0 var(--spacing-32) !important;
      border: 0 !important;
      border-radius: var(--radius-none) !important;
      box-shadow: none !important;
      background: var(--esr-forms-btn-bg, var(--color-dark-oak)) !important;
      color: var(--esr-forms-btn-color, var(--color-white)) !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-button-small) !important;
      line-height: normal !important;
      text-transform: var(--text-transform-button) !important;
    }
    [class*="_formSubmitButton_"]:hover:not(:disabled) {
      background: var(--color-warm-grey) !important;
      color: var(--color-white) !important;
    }

    /* Disclaimer -> Manrope, dark-oak, left aligned */
    [class*="_formDisclaimer_"] {
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-dark-oak) !important;
      text-align: left !important;
    }

    /* Success state (the banner swapped in after submit): the app centres it,
       adds a top margin and paints the heading/body/link in its own type -
       left-align everything and restyle to the theme: heading matches the h5
       style (the Home Club callout heading) and the body matches the section
       subcopy (Manrope small, warm grey) with brand underlined links. */
    [class*="_formBannerContainer_"] {
      margin: 0 !important;
      text-align: left !important;
    }
    [class*="_formBannerHeading_"] {
      /* The app lays the heading out as a grid with centred content, so
         text-align alone can't left-align it. */
      justify-content: start !important;
      margin: 0 0 var(--spacing-12) !important;
      font-family: var(--font-heading) !important;
      font-weight: var(--font-weight-heading) !important;
      font-size: var(--text-h5-mobile) !important;
      letter-spacing: var(--letter-spacing-heading) !important;
      line-height: var(--line-height-heading-mobile) !important;
      text-transform: var(--text-transform-heading) !important;
      text-align: left !important;
      color: var(--esr-forms-input-color, var(--color-dark-oak)) !important;
    }
    @media (min-width: 1104px) {
      [class*="_formBannerHeading_"] {
        font-size: var(--text-h5) !important;
        line-height: var(--line-height-h5) !important;
      }
    }
    [class*="_textBody_"] {
      text-align: left !important;
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-warm-grey) !important;
    }
    [class*="_textBody_"] p {
      margin: 0 !important;
      font: inherit !important;
      color: inherit !important;
    }
    [class*="_textBody_"] p + p {
      margin-top: var(--spacing-8) !important;
    }
    [class*="_textBody_"] a {
      color: var(--esr-forms-input-color, var(--color-dark-oak)) !important;
      text-decoration: underline !important;
      text-underline-offset: 3px !important;
    }

    /* The app's native submit-time field errors -> match the theme's
       .field__message--error (and our on-blur error): Manrope, 12px,
       --color-error. Text/phone fields use the _formError_ class; the custom
       select (Preferred Store) uses its own _error_ class - cover both. */
    [class*="_formError_"],
    [class*="_error_"] {
      font-family: var(--font-body) !important;
      font-weight: var(--font-weight-regular) !important;
      font-size: var(--text-body-small) !important;
      line-height: var(--line-height-body) !important;
      color: var(--color-error) !important;
    }
    /* Error state turns the control's underline red (both submit + blur set
       aria-invalid); covers the focused invalid field too. */
    [class*="_formInputField_"][aria-invalid="true"],
    [class*="_formPhoneInputField_"][aria-invalid="true"],
    [class*="_formInputField_"][aria-invalid="true"]:focus-visible,
    [class*="_formPhoneInputField_"][aria-invalid="true"]:focus-visible {
      border-bottom-color: var(--color-error) !important;
    }
  `;

  // Use each field's own label as its placeholder, minus the trailing
  // "(Required)"/"(Optional)" hint (e.g. "First name (Required)" -> "First name").
  function cleanLabel(text) {
    return text
      .replace(/\s*\((?:required|optional)\)\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function setPlaceholders(root) {
    var inputs = root.querySelectorAll('[class*="_formInputField_"], [class*="_formPhoneInputField_"]');
    inputs.forEach(function (input) {
      var type = (input.getAttribute('type') || 'text').toLowerCase();
      if (type === 'checkbox' || type === 'radio' || type === 'hidden' || type === 'submit' || type === 'button') return;
      if (input.getAttribute('placeholder')) return;
      var label = null;
      var labelledby = input.getAttribute('aria-labelledby');
      if (labelledby) label = root.getElementById(labelledby);
      if (!label) {
        var container = input.closest('[class*="_formFieldContainer_"]');
        if (container) label = container.querySelector('[class*="_formInputFieldLabel_"]');
      }
      var text = label ? cleanLabel(label.textContent) : '';
      if (text) input.setAttribute('placeholder', text);
    });
  }

  // Swap the app's chevron (filled triangle) for the brand chevron. Stroke is
  // currentColor so the CSS rule above tints it with the dark-oak token.
  var ARROW_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M13.5156 4.349L7.5 10.3646L1.48438 4.349" stroke="currentColor"/></svg>';

  function replaceArrows(root) {
    root.querySelectorAll('[class*="_arrowDownIcon_"]').forEach(function (span) {
      if (span.getAttribute('data-es-arrow')) return;
      span.setAttribute('data-es-arrow', '1');
      span.innerHTML = ARROW_SVG;
    });
  }

  // Lock the phone country to the section's choice (AU/NZ) when the merchant
  // hides the selector. CSS already hides it; here we set its value by driving
  // the app's own widget (programmatic click works even though it's
  // display:none). The country options carry stable ids (select-country-AU /
  // -NZ). Guarded so it runs once per selector element, and re-runs if the app
  // swaps the element in (the data attribute is gone on the fresh node).
  function applyPhoneCountry(root, country) {
    var cc = (country || '').toUpperCase();
    if (cc !== 'AU' && cc !== 'NZ') return;
    var sel = root.querySelector('.phone-country-selector');
    if (!sel || sel.getAttribute('data-es-country') === cc) return;
    var img = sel.querySelector('[class*="_selectToggleText_"] img');
    var want = '/flags/' + cc.toLowerCase() + '.svg';
    // Mark first so the mutations from driving the widget don't re-enter.
    sel.setAttribute('data-es-country', cc);
    if (img && (img.getAttribute('src') || '').indexOf(want) !== -1) return; // already set
    var toggle = sel.querySelector('[class*="_selectToggle_"]');
    if (!toggle) return;
    if (toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
    window.setTimeout(function () {
      var opt = root.getElementById('select-country-' + cc);
      if (opt) opt.click();
    }, 100);
  }

  // ---- Query-string prefill ------------------------------------------------
  // The app's fields are React-controlled, so assigning input.value alone is
  // discarded on the next render. Go through the native value setter and fire a
  // bubbling input event, which is what React's onChange listens for. The
  // listener lives inside the shadow root (the app renders there), so the event
  // reaches it; composed is set anyway in case a future build listens outside.
  function setFieldValue(input, value) {
    var desc = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (desc && desc.set) { desc.set.call(input, value); } else { input.value = value; }
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  function queryParam(name) {
    try {
      var value = new URLSearchParams(window.location.search).get(name);
      return value ? value.trim() : '';
    } catch (e) {
      return '';
    }
  }

  function normalizeEmail(value) {
    return (value || '').trim().toLowerCase();
  }

  // The signed-in account's email, rendered by the hosting section onto an
  // ancestor of the embed (see sections/form-media.liquid). Empty when the
  // visitor is signed out, and on hosts that don't render it - both fall back
  // to the plain "fill the empty field" behaviour.
  function accountEmail(embed) {
    var host = embed.closest('[data-esr-forms-account-email]');
    return host ? normalizeEmail(host.getAttribute('data-esr-forms-account-email')) : '';
  }

  // Stop overriding the form as soon as the visitor edits it themselves, so we
  // never fight what they type. Our own writes go through setFieldValue, whose
  // synthetic events are untrusted, so they don't trip this. Capture phase, so
  // the flag is set before the app re-renders off the same event.
  function watchForInput(root, embed) {
    if (embed.__esFormsWatched) return;
    embed.__esFormsWatched = true;
    ['input', 'keydown', 'paste'].forEach(function (type) {
      root.addEventListener(type, function (event) {
        if (event.isTrusted) embed.__esFormsTouched = true;
      }, true);
    });
  }

  // Wipe what the app filled in from the account, so only the address the
  // visitor typed carries across. Text-like controls are emptied; the phone
  // keeps its country prefix (the app renders one with no account too).
  // Checkboxes, radios and selects are left alone - marketing consent and the
  // phone country aren't account details being overridden.
  function clearAccountFields(root, keep) {
    var fields = root.querySelectorAll('[class*="_formInputField_"], [class*="_formPhoneInputField_"]');
    fields.forEach(function (input) {
      if (input === keep || input.type === 'checkbox' || input.type === 'radio') return;
      if (input.type === 'tel') {
        var prefix = /^\+\d+\s*/.exec(input.value || '');
        if (prefix) {
          if (input.value !== prefix[0]) setFieldValue(input, prefix[0]);
          return;
        }
      }
      if (input.value) setFieldValue(input, '');
    });
  }

  // Find the email control. type="email" is how the app renders an email field;
  // the label is the fallback, in case a merchant builds it as a text field.
  function emailField(root) {
    var input = root.querySelector('[class*="_formInputField_"][type="email"]');
    if (input) return input;
    var candidates = root.querySelectorAll('[class*="_formInputField_"]');
    for (var i = 0; i < candidates.length; i++) {
      var container = candidates[i].closest('[class*="_formFieldContainer_"]');
      var label = container ? container.querySelector('[class*="_formInputFieldLabel_"]') : null;
      if (label && cleanLabel(label.textContent).toLowerCase().indexOf('email') !== -1) {
        return candidates[i];
      }
    }
    return null;
  }

  // Signed out (no account email to compare against), fill once per embed and
  // only into an empty field: decorate() re-runs on every app re-render, and
  // re-filling would overwrite what the visitor typed (or put back an address
  // they deliberately cleared).
  //
  // Signed in, the app fills the form from the account, so the two addresses
  // are compared. The same address means the account's details are the ones
  // wanted, so the form is left exactly as the app rendered it. A different
  // address means the one the visitor just typed wins: it replaces the account
  // email and the account's other details are cleared. That override is
  // re-applied on later passes - the app can fill fields in after its first
  // render - until the visitor edits the form themselves.
  function prefillFromQuery(root, embed) {
    if (!embed || embed.__esFormsPrefilled) return;
    var email = queryParam('email');
    if (!email) { embed.__esFormsPrefilled = true; return; }
    var account = accountEmail(embed);
    if (account && normalizeEmail(email) === account) { embed.__esFormsPrefilled = true; return; }
    var input = emailField(root);
    if (!input) return; // fields may still be streaming in; a later pass gets it
    if (!account) {
      embed.__esFormsPrefilled = true;
      if (input.value) return;
      setFieldValue(input, email);
      return;
    }
    watchForInput(root, embed);
    if (embed.__esFormsTouched) { embed.__esFormsPrefilled = true; return; }
    clearAccountFields(root, input);
    if (normalizeEmail(input.value) !== normalizeEmail(email)) setFieldValue(input, email);
  }

  function decorate(root, embed) {
    setPlaceholders(root);
    replaceArrows(root);
    prefillFromQuery(root, embed);
  }

  function setup(embed) {
    var root = embed.shadowRoot;
    if (!root) return false;
    if (!root.querySelector('style[data-es-forms]')) {
      var style = document.createElement('style');
      style.setAttribute('data-es-forms', '');
      style.textContent = FORM_CSS;
      root.appendChild(style);
    }
    var lockHost = embed.closest('[data-phone-country-lock]');
    var lockCountry = lockHost ? lockHost.getAttribute('data-phone-country-lock') : null;
    decorate(root, embed);
    if (lockCountry) applyPhoneCountry(root, lockCountry);
    // Re-apply if the app re-renders or adds fields later.
    if (!embed.__esFormsObserved) {
      embed.__esFormsObserved = true;
      new MutationObserver(function () {
        decorate(root, embed);
        if (lockCountry) applyPhoneCountry(root, lockCountry);
      })
        .observe(root, { childList: true, subtree: true });
    }
    // Reveal only once the form has actually rendered a field. Our style is
    // already in the shadow root, so by the time a field exists it is styled -
    // revealing now avoids the unstyled flash. Until then keep waiting: the
    // app injects the embed and streams the fields in after the page loads.
    if (!root.querySelector('[class*="_formInputField_"], [class*="_formSubmitButton_"]')) {
      return false;
    }
    var container = embed.closest('[data-esr-forms-reveal]');
    if (container) container.classList.add('is-ready');
    return true;
  }

  // A container that hosts the Forms app block WILL get an embed + fields, so
  // it must wait for setup() - only reveal containers with nothing to wait on.
  function revealLeftover() {
    document.querySelectorAll('[data-esr-forms-reveal]').forEach(function (c) {
      if (!c.querySelector('.shopify-app-block, shopify-forms-embed')) {
        c.classList.add('is-ready');
      }
    });
  }

  function init() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    if (!embeds.length) return false;
    var done = true;
    embeds.forEach(function (embed) { if (!setup(embed)) done = false; });
    return done;
  }

  if (!init()) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      // Give up after ~12s. If the form never rendered, reveal anyway as a
      // last resort so the container (and any intro content) is never lost.
      if (init() || tries > 60) {
        clearInterval(timer);
        if (tries > 60) {
          document.querySelectorAll('[data-esr-forms-reveal]').forEach(function (c) {
            c.classList.add('is-ready');
          });
        }
      }
    }, 200);
  }

  revealLeftover();
})();
